import useVpnStore from '@/stores/vpn';

import buildStationObject from './build_station_object';
import nateFetch from './nate_fetch';

// When NATE runner state is not loaded before this timeout it is considered as not running.
const DEFAULT_NATE_RUNNER_REQUEST_TIMEOUT = 10000;

// When NATE state is not loaded before this timeout it is considered as not running.
const DEFAULT_NATE_REQUEST_TIMEOUT = 10000;

class StationError extends Error {
  constructor({ host, status, cause = null }) {
    super();

    if (Error.captureStackTrace) Error.captureStackTrace(this, StationError);

    this.name = 'StationError';
    this.host = host;
    this.status = status;
    this.cause = cause;
  }

  toStationObject() {
    return buildStationObject({ host: this.host, status: this.status });
  }
}

// Proxy-generated error codes that mean "the station is unreachable" (short-circuited
// on a stale WireGuard handshake, or the upstream fetch failed). Mapped to the same
// offline statuses a direct-mode timeout produces; any other non-ok response keeps the
// generic fetch-error status.
const PROXY_OFFLINE_ERROR_CODES = ['station_offline', 'upstream_request_failed'];

async function throwForErrorResponse({ response, host, offlineStatus, errorStatus }) {
  if (response.ok) return;

  const body = await response.json().catch(() => null);
  const status = PROXY_OFFLINE_ERROR_CODES.includes(body?.error) ? offlineStatus : errorStatus;

  throw new StationError({ host, status });
}

async function refreshStation({
  host,
  nateRunnerRequestTimeout = DEFAULT_NATE_RUNNER_REQUEST_TIMEOUT,
  nateRequestTimeout = DEFAULT_NATE_REQUEST_TIMEOUT,
}) {
  const { mode, activeBaseUrl } = useVpnStore();
  const number = host.split('.')[2];

  // Proxy mode routes through nate-vpn-proxy by station number; direct mode hits the raw VPN address.
  const nateRunnerApiUrl = mode === 'proxy' ? `${activeBaseUrl}/proxy/${number}/nate_runner` : `http://${host}`;

  let nateRunnerStateResponse;
  try {
    nateRunnerStateResponse = await nateFetch(`${nateRunnerApiUrl}/state`, {
      signal: AbortSignal.timeout(nateRunnerRequestTimeout),
    });
  } catch (error) {
    // AbortSignal.timeout aborts the request (not just stops awaiting), freeing the connection.
    if (error.name === 'TimeoutError') throw new StationError({ host, status: 'nate_runner_not_running' });

    throw new StationError({ host, cause: error, status: 'nate_runner_state_fetch_error' });
  }

  await throwForErrorResponse({
    response: nateRunnerStateResponse,
    host,
    offlineStatus: 'nate_runner_not_running',
    errorStatus: 'nate_runner_state_fetch_error',
  });

  let nateRunnerState;
  try {
    nateRunnerState = await nateRunnerStateResponse.json();
  } catch (error) {
    throw new StationError({ host, cause: error, status: 'nate_runner_state_json_error' });
  }

  // Proxy mode ignores the runner-reported internal api_url and routes by station number instead.
  if (mode !== 'proxy' && !nateRunnerState?.data?.nate?.api_url) {
    console.warn(host, nateRunnerState);

    throw new StationError({ host, status: 'nate_runner_state_missing_api_url' });
  }

  const nateApiUrl = mode === 'proxy' ? `${activeBaseUrl}/proxy/${number}/nate` : nateRunnerState?.data?.nate?.api_url;

  let nateStateResponse;
  try {
    nateStateResponse = await nateFetch(`${nateApiUrl}/state`, {
      signal: AbortSignal.timeout(nateRequestTimeout),
    });
  } catch (error) {
    if (error.name === 'TimeoutError') throw new StationError({ host, status: 'nate_not_running' });

    throw new StationError({ host, cause: error, status: 'nate_state_fetch_error' });
  }

  await throwForErrorResponse({
    response: nateStateResponse,
    host,
    offlineStatus: 'nate_not_running',
    errorStatus: 'nate_state_fetch_error',
  });

  let nateState;
  try {
    nateState = await nateStateResponse.json();
  } catch (error) {
    throw new StationError({ host, cause: error, status: 'nate_state_json_error' });
  }

  return buildStationObject({ host, status: 'ok', nateRunnerApiUrl, nateRunnerState, nateApiUrl, nateState });
}

export default async function safeRefreshStation({
  host,
  nateRunnerRequestTimeout = DEFAULT_NATE_RUNNER_REQUEST_TIMEOUT,
  nateRequestTimeout = DEFAULT_NATE_REQUEST_TIMEOUT,
}) {
  return refreshStation({ host, nateRunnerRequestTimeout, nateRequestTimeout }).catch((error) => {
    if (error.name !== 'StationError') console.warn(error);

    const stationError =
      error.name === 'StationError' ? error : new StationError({ host, cause: error, status: 'unknown_error' });

    return stationError.toStationObject();
  });
}
